// src/pages/auth/LoginPage.jsx
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { authApi } from '@/api/services'
import { Button, Input } from '@/components/common/ui'
import toast from 'react-hot-toast'

const schema = z.object({
  email:    z.string().email('Enter a valid email'),
  password: z.string().min(6, 'Password is required'),
})

export default function LoginPage() {
  const { login } = useAuth()
  const navigate  = useNavigate()
  const location  = useLocation()
  const from      = location.state?.from?.pathname || null

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (values) => {
    try {
      const user = await login(values.email, values.password)
      toast.success(`Welcome back, ${user.name.split(' ')[0]}!`)
      if (from) return navigate(from, { replace: true })
      navigate(user.role === 'admin' ? '/admin' : user.role === 'supplier' ? '/supplier' : '/buyer', { replace: true })
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid credentials')
    }
  }

  return (
    <div className="card p-8">
      <h1 className="font-display font-bold text-2xl mb-1">Welcome back</h1>
      <p className="text-sm text-muted mb-7">Sign in to your Fabrinza account</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input label="Email address" type="email" placeholder="you@example.com"
          error={errors.email?.message} {...register('email')} />
        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label className="text-sm font-semibold">Password</label>
            <Link to="/forgot-password" className="text-xs text-pink hover:underline">Forgot password?</Link>
          </div>
          <input type="password" placeholder="••••••••" className={`input ${errors.password ? 'border-red-400' : ''}`} {...register('password')} />
          {errors.password && <p className="text-red-600 text-xs mt-1">{errors.password.message}</p>}
        </div>

        <Button type="submit" loading={isSubmitting} className="w-full justify-center py-3">
          Sign In
        </Button>
      </form>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
        <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-muted">or continue with</span></div>
      </div>

      <button
        type="button"
        onClick={async () => {
          try {
            const { data } = await authApi.socialRedirect('google')
            window.location.href = data.url
          } catch { toast.error('Could not connect to Google') }
        }}
        className="w-full flex items-center justify-center gap-2 border border-border rounded-xl py-2.5 text-sm font-medium hover:bg-surface transition-colors"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
        Continue with Google
      </button>

      <p className="text-center text-sm text-muted mt-6">
        Don't have an account? <Link to="/register" className="text-pink font-bold hover:underline">Join free</Link>
      </p>
    </div>
  )
}
